<template lang="pug">
.resource-fields
  BriqInput(
    v-if="isVisible('Key')"
    label="Key"
    name="Key"
    v-model="resource.Key"
    :hideOptionalMarker="true"
    :disabled="isFieldDisabled('Key')"
    )
  BriqInput(
    v-if="isVisible('Description')"
    data-test-id="resource-name"
    label="Name"
    name="Description"
    v-model="resourceName"
    required
    :disabled="isFieldDisabled('Description')"
    :error="nameError"
    @blur="vuelidate.resource.Description.$touch()"
    )
  BriqInput(
    v-if="isVisible('Slug')"
    data-test-id="slug"
    label="Slug"
    v-model="resource.Slug"
    required
    :disabled="isFieldDisabled('Slug')"
    edit-input
    help="Only lower case letters, numbers and hyphens"
    :error="slugError"
    @blur="vuelidate.resource.Slug.$touch()"
    )
  BriqCheckbox(
    v-if="isVisible('IsActive')"
    label="Active"
    v-model="resource.IsActive"
    :disabled="isFieldDisabled('IsActive')" 
    )
  BriqAlert.is-active-warning-label(
    v-if="!resource.IsActive && isVisible('IsActive') && !hasMultilanguage"
    variant="warning"
    ) Warning. Disabling a resource also affects products and deals.
  EditResourceAvatar(
    v-if="isVisible('Color') && isVisible('Abbreviation')"
    :color="resource.Color",
    :colors="colors"
    :disabled="isFieldDisabled('Color')"
    :abbreviation="resource.Abbreviation"
    @updateColor="updateColor"
    @updateLetters="updateLetters"
    )
  BriqInput(
    v-if="isVisible('TeamSize')"
    data-test-id="team-size"
    label="Team size"
    name="TeamSize"
    v-model="resource.TeamSize"
    required
    type="number"
    placeholder="0"
    step="1"
    min="0"
    help="The max size for teams. 0 means teams are not used"
    :disabled="isFieldDisabled('TeamSize') || resource.Exclusive"
    :error="teamSizeError"
    @blur="vuelidate.resource.TeamSize.$touch()"
    )
</template>

<script>
import EditResourceAvatar from './EditResourceAvatar.vue'
import BriqDurationInput from '@v/components/BriqDurationInput.vue'
import resourceColors from './resourceColors.js'
import slugify from '@/helpers/slugify.js'
import isFieldDisabled from './isFieldDisabled.js'

export default {
  name: 'ResourceFields',
  components: { EditResourceAvatar, BriqDurationInput },
  props: {
    buKey: String,
    resource: { type: Object, required: true },
    disabled: Boolean,
    hasMultilanguage: Boolean,
    // This is used as workaround for the translation column that needs to respond to resource type as well (the disabled and hidden fields)
    resourceTypeFallback: String,
    resourceTypes: { type: Array, required: true },
    vuelidate: { type: Object, required: true },
  },
  data() {
    return {
      colors: resourceColors,
      shouldSetSlugAutomatically: !this.resource.Slug,
    }
  },
  computed: {
    displayedFields() {
      const type = this.resource.ResourceType || this.resourceTypeFallback
      return this.resourceTypes.find(resourceType => resourceType.ResourceType == type)
        ?.ActiveFields
    },
    resourceName: {
      get() {
        return this.resource.Description
      },
      set(val) {
        this.resource.Description = val
        this.maybeUpdateSlug(val)
      },
    },
    nameError() {
      if (!this.vuelidate.resource.Description?.$error || this.hasMultilanguage) return null
      return 'Please enter a name'
    },
    slugError() {
      return (
        (this.vuelidate.resource.Slug.$dirty &&
          this.vuelidate.resource.Slug.$invalid &&
          'Slug should consist of only lowercase letters, numbers and hyphens') ||
        null
      )
    },
    teamSizeError() {
      if (!this.vuelidate.resource.TeamSize?.$error) return null
      return 'Please enter a team size'
    },
    isValid() {
      return !this.vuelidate.$invalid
    },
  },
  methods: {
    isVisible(fieldName) {
      return this.displayedFields.includes(fieldName)
    },
    isFieldDisabled(fieldName) {
      if (this.disabled) return true
      const resourceType = this.resource.ResourceType || this.resourceTypeFallback
      return isFieldDisabled(resourceType)(fieldName)
    },
    updateColor(newColor) {
      this.$set(this.resource, 'Color', newColor)
    },
    updateLetters(newLetters) {
      this.$set(this.resource, 'Abbreviation', newLetters)
    },
    maybeUpdateSlug(val) {
      if (this.shouldSetSlugAutomatically && !this.hasMultilanguage) {
        this.resource.Slug = slugify(val)
      }
    },
  },
  watch: {
    'resource.Exclusive': function(newVal) {
      if (newVal) this.resource.TeamSize = 0
    },
  },
}
</script>

<style lang="stylus">
@import '~@v/variables.styl'

.resource-fields .briq-form-element
  margin-bottom space-S

.is-active-warning-label
  margin-top -24px
  margin-bottom 24px
</style>
